import { AggregateRoot, UniqueEntityId } from "@kernel/ddd";
import {
  LocalParticipant,
  Participant,
  ParticipantList,
  RemoteParticipant
} from "../participant";
import { RoomNameService } from "./services";

export type RemoteParticipantList = ParticipantList<RemoteParticipant>;
export interface RoomProps {
  localParticipant: LocalParticipant | null;
  remoteParticipants: RemoteParticipantList;
  createdAt: Date;
}

export class Room extends AggregateRoot<RoomProps> {
  public get name(): string {
    return this.id.toString();
  }

  public get localParticipant(): LocalParticipant | null {
    return this.props.localParticipant;
  }

  public set localParticipant(participant: LocalParticipant | null) {
    this.props.localParticipant = participant;
  }

  public get remoteParticipants(): RemoteParticipantList {
    return this.props.remoteParticipants;
  }

  public get countParticipants(): number {
    if (this.props.localParticipant === null) return 0;
    return this.props.remoteParticipants.items.length + 1;
  }

  public get createdAt(): Date {
    return this.props.createdAt;
  }

  public isLocalParticipantPresent(): boolean {
    return this.localParticipant !== null;
  }

  public isRemoteParticipantPresent(): boolean {
    return this.remoteParticipants.items.length > 0;
  }

  public hasRemoteParticipant(remoteId: UniqueEntityId): boolean {
    return this.remoteParticipants.hasByRemoteId(remoteId);
  }

  public getRemoteParticipant(remoteId: number): Participant | null {
    return this.remoteParticipants.getParticipantByRemoteId(remoteId);
  }

  public getPinnedParticipant(): Participant | null {
    const list = ParticipantList.create([
      ...(this.localParticipant !== null ? [this.localParticipant] : []),
      ...this.remoteParticipants.items
    ]);
    return list.getPinnedParticipant();
  }

  public getThumbnailParticipants(): ParticipantList<Participant> {
    const list = ParticipantList.create([
      ...(this.localParticipant !== null ? [this.localParticipant] : []),
      ...this.remoteParticipants.items
    ]);
    return list.getFullfilledParticipants();
  }

  public getRemoteParticipants(): RemoteParticipantList {
    return this.remoteParticipants.getRemoteParticipants();
  }

  public joinLocalParticipant(participant: LocalParticipant): void {
    this.props.localParticipant = participant;
  }

  public joinRemoteParticipant(participant: RemoteParticipant): void {
    this.props.remoteParticipants.add(participant);
  }

  public leaveLocalParticipant(): void {
    this.props.localParticipant = null;
  }

  public leaveRemoteParticipant(participant: RemoteParticipant): void {
    this.props.remoteParticipants.remove(participant);
  }

  public clone(): Room {
    const props = { ...this.props };
    return new Room(props, new UniqueEntityId(this._id.toValue()));
  }

  public toString(): string {
    return this.id.toString();
  }

  public static create(props: RoomProps, id?: UniqueEntityId): Room {
    return new Room(props, id);
  }

  public static createFromRoom(room: Room): Room {
    return new Room(
      {
        localParticipant: room.localParticipant,
        remoteParticipants: room.remoteParticipants,
        createdAt: room.createdAt
      },
      new UniqueEntityId(room.id.toValue())
    );
  }

  public static createEmpty(name?: string): Room {
    name = name || RoomNameService.generateRandomName();
    return new Room(
      {
        localParticipant: null,
        remoteParticipants: ParticipantList.create(),
        createdAt: new Date()
      },
      new UniqueEntityId(name)
    );
  }
}
